// «En llamada» around this tab's calls (shared/API.md §38). The worker decides against the store's
// own user row (data/features/areas.ts); this file only follows the call phase:
//   connected → claim (only over empty / expired / Core's calendar status);
//   every 5 min while connected → refresh, only while the status is still ours;
//   any end → clear, only if still ours; tab unloading → best-effort keepalive DELETE.
// Web only: the Windows call host's native app owns this rule for itself. Never blocks the call UI;
// the worker logs failures.
import { data } from '../../../bridge/client';
import { CALL_STATUS_REFRESH_MS, CallStatusClaim } from '../../../core/callStatusClaim';
import { keepaliveRequest } from '../../keepalive';
import type { CallManager } from './callManagerCore';

export function wireCallStatus(manager: CallManager): void {
  let phase: string = manager.getState().phase;
  /** This tab claimed the status for the current call (so its end may clear it). */
  let claimed = false;
  let timer: ReturnType<typeof setInterval> | null = null;

  const stopTimer = () => {
    if (timer) clearInterval(timer);
    timer = null;
  };

  const release = () => {
    stopTimer();
    if (!claimed) return;
    claimed = false;
    void data.call('callStatusRelease').catch(() => undefined);
  };

  manager.subscribe(() => {
    const next = manager.getState().phase;
    const step = CallStatusClaim.step(phase, next);
    phase = next;
    if (step === 'claim') {
      void data
        .call('callStatusClaim')
        .then((ok) => {
          if (!ok) return;
          claimed = true;
          // The call ended while the claim was in flight: give it straight back.
          if (phase !== 'active') {
            release();
            return;
          }
          stopTimer();
          timer = setInterval(() => {
            void data.call('callStatusRefresh').then(
              (still) => {
                // Someone set another status meanwhile: it is theirs now, never touch it again.
                if (!still) {
                  claimed = false;
                  stopTimer();
                }
              },
              () => undefined,
            );
          }, CALL_STATUS_REFRESH_MS);
        })
        .catch(() => undefined);
    } else if (step === 'release') {
      release();
    }
  });

  window.addEventListener('pagehide', () => {
    if (!claimed) return;
    claimed = false;
    stopTimer();
    keepaliveRequest('DELETE', '/api/v4/users/me/status/custom');
  });
}
